import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { useState } from 'react';
import {
  ArrayInput,
  BooleanInput,
  Edit,
  FormDataConsumer,
  FormTab,
  ImageField,
  ImageInput,
  Labeled,
  NumberInput,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  SelectArrayInput,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextField,
  TextInput,
  minValue,
  useGetOne,
  useNotify,
  useRecordContext,
  useTranslate
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { ImageInformations, PhoneInput, PriceInput, Tooltip, Wrapper } from '../../components';
import DateTimeInputUTC from '../../components/DateTimeInputUTC';
import Permission from '../../components/Permission';
import { RichTextInputCustom } from '../../components/RichTextInputCustom';
import { DEFAULT_API_ADMIN, PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import { post } from '../../lib/request';
import { TagType } from '../../models';
import HomeItemsInput from './HomeItemsInput';
import { buildNavigationChoices } from './HomeItemsInput/homeItems';

const cardsSpoilerSx = {
  width: '150%',
  maxWidth: 500,
  paddingInline: 8,
  marginBottom: 16
};

const cardsHintSx = {
  marginInline: 1,
  marginBottom: 1,
};

const imageCardSx = {
  marginTop: 16,
  '& img': {
    maxWidth: '100%',
  }
};

const ImagePrev = ({ label, imagePath }) => (
  <Labeled label={label} sx={imageCardSx} >
    <img src={imagePath} />
  </Labeled>);

// Campos que vivem no Remote Config do Firebase. Redes sem service account
// cadastrada não têm Remote Config — nesses casos os campos são ocultados.
const FirebaseOnly = ({ children }) => {
  const record = useRecordContext();
  if (record && record.hasFirebaseConfig === false) { return null; }
  return children;
};

const FirebaseMissingAlert = () => {
  const record = useRecordContext();
  if (!record || record.hasFirebaseConfig !== false) { return null; }
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      Esta rede não possui integração com o Firebase configurada. As opções que dependem do
      Remote Config (cards da home, textos e ajustes do app) ficam indisponíveis até que o
      arquivo de service account seja cadastrado no cadastro da rede.
    </Alert>
  );
};

export default () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const translate = useTranslate();
  // O registro editado aqui é o chains-config; as flags de módulo moram no cadastro
  // da rede, que é outro recurso.
  const { data: chain } = useGetOne('chains', { id: getChainId() }, { enabled: !!getChainId() });

  const orderCards = (settings) => {
    if (!settings.firebaseConfig?.home_cards) { return settings; }
    const cardsArr = settings.firebaseConfig.home_cards;

    if (cardsArr && cardsArr.length) {
      cardsArr.forEach((card, index) => {
        card.index = index;
        if (card.imagefile) {
          card.updateImage = true;
        }
      });
    }

    return settings;
  };

  const SgaTriggerButton = () => {
    const [loading, setLoading] = useState(false);

    const handleTriggerSga = async () => {
      setLoading(true);
      try {
        await post(`${DEFAULT_API_ADMIN}/chains/sga/trigger-job`, {});
        notify('Job SGA iniciado com sucesso!', { type: 'success' });
      } catch (error) {
        notify('Erro ao iniciar job SGA', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    return (
      <Button
        variant="contained"
        color="primary"
        onClick={handleTriggerSga}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
        sx={{ mt: 2, mb: 2 }}
      >
        {loading ? 'Executando...' : 'Executar Job SGA Agora'}
      </Button>
    );
  };

  // Destino de card e de bloco só lista o que a rede contratou: a tela do módulo
  // desligado continua existindo no app e abriria vazia. O destino JÁ salvo entra na
  // lista mesmo assim (`keepKey`), senão o seletor abre em branco e o operador não vê
  // para onde o card aponta.
  const navigationChoices = keepKey => buildNavigationChoices(chain, keepKey);

  const onSuccess = () => {
    notify('ra.notification.updated', { messageArgs: { smart_count: 1 } });
    navigate('/settings');
  }

  const onError = () => {
    notify('ra.notification.data_provider_error', { type: 'error' });
    navigate('/settings');
  }

  return (
    <Permission permission={PERMISSIONS.SETTINGS}>
      {
        getChainId() ?
          <Edit
            id={getChainId()}
            resource="chains-config"
            title="Configurações"
            mutationMode='pessimistic'
            mutationOptions={{ onSuccess, onError }}
            transform={(props) => {
              return orderCards(props);
            }}
          >
            {/* <TabbedForm toolbar={<CustomToolbar />}> */}
            <TabbedForm>
              <FormTab label="Geral">
                <ReferenceInput
                  source="defaultStationId"
                  reference="stations"
                  sort={{ field: 'name', order: 'ASC' }}
                  perPage={9999}
                >
                  <SelectInput
                    label="Posto padrão para vínculo de pontos"
                    optionText="name"
                    style={{ minWidth: 290 }}
                  />
                </ReferenceInput>
                <NumberInput
                  source="pointsExpirationTime"
                  label="Tempo para expiração de pontos (dias)"
                  style={{ minWidth: 290 }}
                  min={0}
                />
                <NumberInput
                  source="couponExpirationDays"
                  label="Tempo para expiração de cupons (dias)"
                  style={{ minWidth: 290 }}
                  min={0}
                />
                <PriceInput source="cashbackFactor" label="Valor do cashback para 1 ponto" precision={3} style={{ minWidth: 290 }} />
                <TextInput
                  source="mex10Token"
                  label="Token Mex10"
                  style={{ minWidth: 290 }}
                />
                <Labeled label="Regras de abastecimento">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.pointsWhenDiscounting')}>
                      <BooleanInput label="Pontuar quando usar desconto" source="pointsWhenDiscounting" />
                    </Tooltip>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.usePendingTransaction')}>
                      <BooleanInput label="Solicitar aprovação para abastecimento manual/API" source="usePendingTransaction" />
                    </Tooltip>
                  </>
                </Labeled>
                <Labeled label="Regras de cupons e ofertas">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.useChronologyCouponInvoice')}>
                      <BooleanInput label="Ativar cronologia dos cupons" source="useChronologyCouponInvoice" />
                    </Tooltip>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.useOnlyRedeemAfterFillin')}>
                      <BooleanInput label="Resgate somente após um abastecimento" source="useOnlyRedeemAfterFillin" />
                    </Tooltip>
                  </>
                </Labeled>
                <Labeled label="Cadastro de clientes">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.pointsWhenDiscounting')}>
                      <BooleanInput label="Cadastrar senha automática para o cliente(6 primeiros dígitos do CPF)" source="useGeneratedDefaultPassword" />
                    </Tooltip>
                  </>
                </Labeled>
                <Labeled label="Relatórios">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.useDateConcentrator')}>
                      <BooleanInput label="Usar data do concentrador nos relatórios" source="useDateConcentrator" />
                    </Tooltip>
                  </>
                </Labeled>
              </FormTab>
              <FormTab label="Regulamento">
                <RichTextInputCustom source="regulation" label="Regulamento" />
              </FormTab>
              <FormTab label="Indicação">
                <NumberInput
                  source="referralValue"
                  label="Valor para quem indica"
                  min={0}
                  style={{ width: 300 }}
                />
                <NumberInput
                  source="refereeValue"
                  label="Valor para indicado"
                  min={0}
                  style={{ width: 300 }}
                />
                <FirebaseOnly>
                  <>
                    <Typography fullWidth>Crie o seu texto de indicação. A palavra "referral_code" será substituída pelo código do usuário.</Typography>
                    <TextInput
                      label="Texto de Indicação"
                      source="firebaseConfig.referral_message"
                      fullWidth
                      multiline
                      defaultValue={"Ganhe desconto ao abastecer! Use o meu código referral_code e ganhe ainda mais pontos ao utilizar o app. Garanta a qualidade e quantidade do combustível e troque pontos por produtos e serviços."} />
                  </>
                </FirebaseOnly>
              </FormTab>
              <FormTab label="Aplicativo">
                <FirebaseMissingAlert />
                <FirebaseOnly>
                <Accordion sx={cardsSpoilerSx}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Cards na Tela Inicial do App</Typography>
                  </AccordionSummary>
                  <Alert severity="info" sx={cardsHintSx}>
                    Card com etiqueta fica escondido para quem está em versão antiga do aplicativo —
                    a segmentação por etiqueta depende de o cliente atualizar o app. Já a validade vale
                    para todas as versões: quem tira o card do ar na data é o servidor.
                  </Alert>
                  <ArrayInput label="É necessário reabrir o App para visualizar as alterações" source="firebaseConfig.home_cards">
                    <SimpleFormIterator style={{ margin: 8 }}>
                      <FormDataConsumer>
                        {({ scopedFormData }) => (
                          <>
                            <ImagePrev
                              label="Imagem Atual"
                              {...scopedFormData}
                            />
                            <ImageInput label="Nova Imagem" record={scopedFormData} source="imagefile" accept={{ 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] }}>
                              <ImageField source="src" />
                            </ImageInput>
                            <ImageInformations position={-20} maxSizeMb={10} />
                            <SelectInput
                              label="Metodo de Navegação"
                              source="navigation.method"
                              choices={[
                                { id: 'screen', name: 'Tela' },
                                { id: 'webview', name: 'Site Externo' },
                                { id: 'whatsapp', name: 'Whatsapp Externo' },
                              ]}
                              defaultValue="screen"
                              style={{ width: '100%' }}
                            />
                          </>
                        )}
                      </FormDataConsumer>
                      <FormDataConsumer>
                        {({ scopedFormData }) => {
                          switch (scopedFormData?.navigation?.method) {
                            case 'screen':
                              return (
                                <SelectInput
                                  label="Destino da Navegação"
                                  style={{ width: '100%' }}
                                  source="navigation.destination"
                                  choices={navigationChoices(scopedFormData?.navigation?.destination)}
                                  defaultValue=""
                                />
                              );
                            case 'whatsapp':
                              return (
                                <>
                                  <PhoneInput label="Número de Destino" record={scopedFormData} source="navigation.destination" style={{ width: '100%' }} />
                                  <TextInput label="Mensagem Inicial" record={scopedFormData} source="navigation.whatsappMessage" style={{ width: '100%' }} />
                                </>
                              );
                            case 'webview':
                              return (
                                <TextInput label="URL de Destino" record={scopedFormData} source="navigation.destination" style={{ width: '100%' }} />
                              );
                            default:
                              return null;
                          }
                        }}
                      </FormDataConsumer>
                      <ReferenceArrayInput
                        reference="chains/chainId/customer-groups"
                        source="groupIds"
                        perPage={null}
                      >
                        <SelectArrayInput optionText="name" label="Exibir card apenas para grupos" style={{ minWidth: 350 }} />
                      </ReferenceArrayInput>
                      <ReferenceArrayInput
                        reference="chains/chainId/tag"
                        source="tagIds"
                        sort={{ field: 'name', order: 'ASC' }}
                        filter={{ type: TagType.client }}
                        perPage={null}
                      >
                        <SelectArrayInput
                          optionText="name"
                          label="Exibir card apenas para etiquetas"
                          helperText="Com etiqueta preenchida, o grupo acima deixa de valer."
                          style={{ minWidth: 350 }}
                        />
                      </ReferenceArrayInput>
                      <DateTimeInputUTC
                        source="startAt"
                        label="Exibir a partir de"
                        helperText="Em branco, o card já entra no ar."
                        style={{ minWidth: 350 }}
                      />
                      <DateTimeInputUTC
                        source="endAt"
                        label="Exibir até"
                        helperText="Passada a data, o card sai do app sozinho."
                        style={{ minWidth: 350 }}
                      />
                    </SimpleFormIterator>
                  </ArrayInput>
                </Accordion>
                </FirebaseOnly>
                <Labeled label="Tela inicial">
                  <>
                    <FirebaseOnly>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.showCashbackAtHome')}>
                        <BooleanInput label="Saldo em Cashback na Home do app" source="firebaseConfig.show_cashback_at_home" defaultValue={false} />
                      </Tooltip>
                    </FirebaseOnly>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.appForceNotificationPermission')}>
                      <BooleanInput label="Insistir permissão de notificação" source="appForceNotificationPermission" defaultValue={false} />
                    </Tooltip>
                  </>
                </Labeled>
                <Labeled label="Avaliação">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.requireRatingOnFillin')}>
                      <BooleanInput label="Obrigar usuário a avaliar abastecimento" source="requireRatingOnFillin" />
                    </Tooltip>
                    <FirebaseOnly>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.shouldShowStationRating')}>
                        <BooleanInput label="Mostrar avaliação do posto" source="firebaseConfig.should_show_station_rating" defaultValue={false} />
                      </Tooltip>
                    </FirebaseOnly>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.appStoreReviewEnabled')}>
                      <BooleanInput label="Solicitar avaliação nas lojas (App Store/Play Store)" source="appStoreReviewEnabled" defaultValue={false} />
                    </Tooltip>
                  </>
                </Labeled>
                <Labeled label="Abastecimento">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.useFillinPreset')}>
                      <BooleanInput label="Usar preset no abastecimento" source="useFillinPreset" />
                    </Tooltip>
                    <FirebaseOnly>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.discountButtonEnabled')}>
                        <BooleanInput label="Botão de desconto ativado por padrão" source="firebaseConfig.discount_button_enabled" defaultValue />
                      </Tooltip>
                    </FirebaseOnly>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.appAutoExpandDiscountModal')}>
                      <BooleanInput label="Forçar exibição dos descontos no abastecimento" source="appAutoExpandDiscountModal" defaultValue />
                    </Tooltip>
                  </>
                </Labeled>
                <Labeled label="Cupons e Ofertas">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.isOffersEnabledInCoupon')}>
                      <BooleanInput label="Mostrar ofertas na tela de cupons" source="isOffersEnabledInCoupon" />
                    </Tooltip>
                    <FormDataConsumer>
                      {({ formData }) => (
                        formData.isOffersEnabledInCoupon &&
                        <Card variant="outlined" sx={{ backgroundColor: 'grey.100', marginBottom: 2 }}>
                          <CardContent>
                            <Typography gutterBottom sx={{ color: 'text.secondary', fontSize: 14, marginBottom: 2 }}>
                              Configurações da aba de ofertas
                            </Typography>
                            <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.showPricesInApp')}>
                              <BooleanInput label="Mostrar preço final em vez do desconto (combustível)" source="showPricesInApp" />
                            </Tooltip>
                            {
                              formData.showPricesInApp &&
                              <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title={translate('tooltips.priceVisibleGroupIds')}>
                                <ReferenceArrayInput
                                  reference="chains/chainId/customer-groups"
                                  source="priceVisibleGroupIds"
                                  perPage={null}
                                >
                                  <SelectArrayInput optionText="name" label="Exibir preço final apenas para grupos" style={{ minWidth: 350, marginBottom: 0, paddingBottom: 0 }} />
                                </ReferenceArrayInput>
                              </Tooltip>
                            }
                          </CardContent>
                        </Card>
                      )}
                    </FormDataConsumer>
                  </>
                </Labeled>
                <Labeled label="Tela de detalhes do abastecimento">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.showFillinMsgPerLiterDiscount')}>
                      <BooleanInput label="Mostrar desconto por litro" source="showFillinMsgPerLiterDiscount" />
                    </Tooltip>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.showFillinMsgPoints')}>
                      <BooleanInput label="Mostrar Pontuação" source="showFillinMsgPoints" />
                    </Tooltip>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.showFillinMsgCashback')}>
                      <BooleanInput label="Mostrar cashback" source="showFillinMsgCashback" />
                    </Tooltip>
                  </>
                </Labeled>
                <Labeled label="Promoter">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.allowPromoterUnlinking')}>
                      <BooleanInput label="Permitir desassociação de Promoter" source="allowPromoterUnlinking" />
                    </Tooltip>
                  </>
                </Labeled>
                <FirebaseOnly>
                  <Labeled label="Sorteio">
                    <>
                      <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title={translate('tooltips.raffleRegulationUrl')}>
                        <TextInput label="Regulamento do Número da Sorte" source="firebaseConfig.raffle_regulation_url" style={{ minWidth: 350 }} />
                      </Tooltip>
                    </>
                  </Labeled>
                </FirebaseOnly>
              </FormTab>
              <FormTab label="Layout da Home">
                <HomeItemsInput chain={chain} />
              </FormTab>
              <FormTab label="Integrações">
                <FormDataConsumer>
                  {({ formData }) => (
                    formData.useSgaIntegration &&
                    <>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Usuário de acesso à API SGA (Hinova)">
                        <TextInput
                          source="sgaUsername"
                          label="Usuário SGA"
                          style={{ minWidth: 300 }}
                        />
                      </Tooltip>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Senha de acesso à API SGA (Hinova)">
                        <TextInput
                          source="sgaPassword"
                          label="Senha SGA"
                          style={{ minWidth: 300 }}
                          type="password"
                        />
                      </Tooltip>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Token base de autenticação da API SGA (Hinova)">
                        <TextInput
                          source="sgaApiToken"
                          label="Token API SGA"
                          style={{ minWidth: 400 }}
                          type="password"
                        />
                      </Tooltip>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title={translate('tooltips.defaultTagSGA')}>
                        <ReferenceInput
                          source="defaultTagId"
                          reference="chains/chainId/tag"
                          sort={{ field: 'name', order: 'ASC' }}
                          filter={{ type: TagType.client }}
                          // @ts-ignore
                          perPage={null}
                        >
                          <SelectInput
                            style={{ minWidth: 300 }}
                            optionText="name"
                            label="Etiqueta padrão seguradora SGA"
                            emptyText="Nenhum" />
                        </ReferenceInput>
                      </Tooltip>
                      <Permission permission={PERMISSIONS.ADMIN}>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Execução Manual
                          </Typography>
                          <SgaTriggerButton />
                        </Box>
                      </Permission>
                    </>
                  )}
                </FormDataConsumer>
              </FormTab>
              <FormTab label="Métricas de Combustível">
                <ArrayInput
                  source="fuelIntegrationRules"
                  label="Combustíveis"
                >
                  <SimpleFormIterator disableRemove disableReordering disableAdd>
                    <FormDataConsumer>
                      {({ scopedFormData }) => {
                        return (
                          <>
                            <Accordion style={{ width: '100%', marginBottom: 12 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <ReferenceField label="Combustível" link={false} record={scopedFormData} source="fuelId" reference='fuels'>
                                  <TextField source="name" style={{ fontSize: '16px' }} />
                                </ReferenceField>
                              </AccordionSummary>
                              <AccordionDetails>
                                <NumberInput
                                  source="amountToFillingCompletely"
                                  record={scopedFormData}
                                  label="Considerar tanque cheio (L)"
                                  style={{ width: 280 }}
                                  validate={[minValue(0)]}
                                />
                              </AccordionDetails>
                            </Accordion>
                          </>
                        );
                      }}
                    </FormDataConsumer>
                  </SimpleFormIterator>
                </ArrayInput>
              </FormTab>
              <FormTab label="Login no aplicativo">
                <Box display={{ xs: 'block', sm: 'flex' }}>
                  <Box flex={2} mr={{ xs: 0, sm: '1.5em' }}>
                    <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title={translate('tooltips.maxDevicePerCpf')}>
                      <NumberInput
                        label="Máximo de dispositivos por CPF"
                        source="maxDevicePerCpf"
                        min={1}
                        validate={[minValue(1)]}
                        parse={(value) => (isNaN(value) || value <= 0 ? null : Math.round(value))}
                        style={{ minWidth: 300 }}
                      />
                    </Tooltip>
                  </Box>
                  <Box flex={2}>
                    <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title={translate('tooltips.maxCpfPerDevice')}>
                      <NumberInput
                        label="Máximo de CPFs por dispositivo"
                        source="maxCpfPerDevice"
                        min={1}
                        validate={[minValue(1)]}
                        parse={(value) => (isNaN(value) || value <= 0 ? null : Math.round(value))}
                        style={{ minWidth: 300 }}
                      />
                    </Tooltip>
                  </Box>
                </Box>
              </FormTab>
            </TabbedForm>
          </Edit>
          :
          <Wrapper>
            <Typography>Selecione uma rede para fazer alterações</Typography>
          </Wrapper>
      }
    </Permission>
  );
};
